import { getPagesByTime, savePage as doSavePage } from 'shared/api/sites';

export interface RevertSiteParams {
  siteId: number;
  revertSiteVersion: Site;
  updateSite: (siteId: number, site: Site) => void;
  fetchPagesByTime: (siteId: number, time: number) => void;
  saveSite: (siteId: number) => void;
}

/**
 * Executes the full revert flow for a site version:
 * 1. Updates site-level metadata (header, footer, theme) in Redux synchronously.
 * 2. Fetches historical pages with content, saves each to the DB.
 * 3. Loads the saved pages into Redux so the editor reflects the reverted state.
 * 4. Persists site metadata to the DB.
 *
 * @param params - Revert parameters including siteId, the target version, and Redux callbacks.
 * @returns Promise that resolves when the revert is complete.
 * @throws When any API call fails.
 */
export const executeSiteRevert = async ({
  siteId,
  revertSiteVersion,
  updateSite,
  fetchPagesByTime,
  saveSite,
}: RevertSiteParams): Promise<void> => {
  // Update site-level metadata (header, footer, theme) in Redux synchronously
  updateSite(siteId, revertSiteVersion);

  // Fetch historical pages with content (backend returns @JsonView(Expanded));
  // persist each page to the DB, then update Redux to reflect the reverted state.
  const pages = await getPagesByTime(
    siteId,
    revertSiteVersion.lastPublishedDate
  );

  // Only save pages that actually have a published version at this time;
  // pages created after this publish won't have content and are skipped.
  const validPages = pages.filter((p: Page) => p.current?.content != null);
  await Promise.all(
    validPages.map((p: Page) => doSavePage(siteId, p.id, p.current))
  );

  // Load the now-saved historical pages into Redux so the editor reflects them
  fetchPagesByTime(siteId, revertSiteVersion.lastPublishedDate);

  // Save site metadata to DB (reads from Redux, which already has revertSiteVersion)
  saveSite(siteId);
};
